import { LocalizationOptions } from '..';
import { valueAndUOMFormatter } from '../../shared/utils/functions';
import { SpaceCardDetails } from '../../stacking-plan-container/actions-toolbar/settings/settings.model';
import { EDPSpaceInfo, SuiteType } from '../../stacking-plan-container/floor-container/floor/item/item.model';
import { MappedLov } from '../../stacking-plan.model';
import { L10nTranslationService } from 'angular-l10n';

export interface SpaceClassOptions {
  localization: LocalizationOptions;
  translationService: L10nTranslationService;
}

export abstract class AbstractSpaceClass {
  static unknown = '';

  public suiteId: number;
  public suiteNumber: string;
  public suiteDesc: string;
  public occupancyStatus: boolean;
  public availabilityStatus: boolean;
  public suiteType: SuiteType;
  public floorNumber: string;
  public isHighlighted: boolean;
  public estimated: boolean;

  /**
   * Receives a map of fields to display, filters and returns only the keys of
   * the required (turned on) fields to be displayed.
   *
   * @param detailsToDisplay Map of fields to be displayed.
   *                          {@link SpaceCardDetails} for attributes details.
   * @param extraKeyValidation Extra function that runs inside the filter, for
   *                            custom field validation
   */
  protected static getRequiredFieldsToDisplay(
    detailsToDisplay: SpaceCardDetails,
    extraKeyValidation?: (key, isRequired) => boolean
  ): string[] {
    extraKeyValidation = extraKeyValidation != null ? extraKeyValidation : () => true;
    return Object.entries(detailsToDisplay)
      .filter(([key, isRequired]) => isRequired && extraKeyValidation(key, isRequired))
      .map(([key]) => key);
  }

  /**
   * Review if area & UOM exists and return the concatenated value. Return only
   * the area if UOM is not present, else returns null.
   */
  protected constructor(
    edpSpaceInfo: EDPSpaceInfo | Partial<EDPSpaceInfo>,
    suiteType: SuiteType,
    protected options: Partial<SpaceClassOptions> = {}
  ) {
    const {
      suite_id,
      suite_number,
      suite_desc,
      occupancy_status,
      availability_status,
      floor_number,
      is_space_highlighted,
      estimated,
    } = edpSpaceInfo;
    this.suiteId = suite_id;
    this.suiteNumber = suite_number;
    this.suiteDesc = suite_desc;
    this.occupancyStatus = occupancy_status;
    this.availabilityStatus = availability_status;
    this.suiteType = suiteType;
    this.floorNumber = floor_number;
    this.isHighlighted = is_space_highlighted;
    this.estimated = estimated;
  }

  abstract getAreaSpace(): number;
  abstract getAreaSpaceUOM(): string;
  abstract internalSpaceInfoClone(): Pick<EDPSpaceInfo, 'tenant' | 'lease' | 'availability' | 'non_rentable_space'>;
  abstract toString(options?: { [key: string]: any }): string;
  abstract enableEditEvent(): boolean;

  public toEDPSpaceInfo(): EDPSpaceInfo {
    return {
      suite_id: this.suiteId,
      suite_number: this.suiteNumber,
      suite_desc: this.suiteDesc,
      occupancy_status: this.occupancyStatus,
      availability_status: this.availabilityStatus,
      estimated: this.estimated,
      ...this.internalSpaceInfoClone(),
      floor_number: this.floorNumber,
    };
  }

  public generateFloorSpace(areaUomLov?: MappedLov) {
    return valueAndUOMFormatter('getAreaSpace', 'getAreaSpaceUOM', this, areaUomLov);
  }

  public getSuiteNumber(options: Partial<SpaceClassOptions>): string {
    const translatedSuite = options?.translationService?.translate('overlayDetails.spaceLocation') || 'Suite';
    return `${translatedSuite} ${this.suiteNumber}`;
  }

  public getFloorNumber(): string {
    return this.floorNumber ?? null;
  }
}
